/**
 * sensor.community : capteurs de particules installés par des particuliers.
 *
 * Ils ne sont pas homologués et sont moins fiables individuellement qu'une
 * station officielle, mais ils sont nombreux et rapides. C'est ce qui permet
 * de dire si la fumée est effectivement descendue jusqu'au sol, là où la
 * carte des feux ne dit que où se trouve le risque.
 */
import { SENSOR_RADIUS_KM } from '../core/constants';
import { distanceKm } from '../core/geo';
import type { SensorReading } from '../core/types';
import { getJson } from './http';

const ENDPOINT = 'https://data.sensor.community/airrohr/v1/filter';

/** Bornes de plausibilité : au-delà, le capteur est en panne, pas l'air. */
const PM25_MAX = 2000;
const PM10_MAX = 3000;

interface SensorRecord {
  timestamp?: string;
  location?: {
    latitude?: string | number;
    longitude?: string | number;
    indoor?: number;
  };
  sensor?: { id?: number | string };
  sensordatavalues?: { value_type?: string; value?: string | number }[];
}

const toNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'number' ? value : Number.parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

export async function fetchSensors(
  lat: number,
  lon: number,
  radiusKm: number = SENSOR_RADIUS_KM,
): Promise<SensorReading[]> {
  const records = await getJson<SensorRecord[]>(
    `${ENDPOINT}/area=${lat},${lon},${radiusKm}`,
  );
  if (!Array.isArray(records)) return [];

  /*
   * Un même capteur publie plusieurs fois : on ne garde que sa mesure la plus
   * récente. Sans ce dédoublonnage, un capteur bavard pèserait plusieurs fois
   * dans la médiane et fausserait le verdict à lui seul.
   */
  const latestById = new Map<string, SensorReading>();

  for (const record of records) {
    const location = record.location;
    if (!location || location.indoor === 1) continue;

    let pm25: number | null = null;
    let pm10: number | null = null;
    for (const value of record.sensordatavalues ?? []) {
      if (value.value_type === 'P2') pm25 = toNumber(value.value);
      if (value.value_type === 'P1') pm10 = toNumber(value.value);
    }

    if (pm25 === null || pm25 < 0 || pm25 > PM25_MAX) continue;
    if (pm10 !== null && (pm10 < 0 || pm10 > PM10_MAX)) pm10 = null;

    const sensorLat = toNumber(location.latitude);
    const sensorLon = toNumber(location.longitude);
    if (sensorLat === null || sensorLon === null) continue;

    // L'API donne un horodatage UTC sans indicateur de fuseau.
    const at = Date.parse(`${String(record.timestamp).replace(' ', 'T')}Z`);
    if (!Number.isFinite(at)) continue;

    const id = String(record.sensor?.id ?? `${sensorLat},${sensorLon}`);
    const existing = latestById.get(id);
    if (!existing || at > existing.at) {
      latestById.set(id, {
        id,
        at,
        pm25,
        pm10,
        distanceKm: distanceKm(lat, lon, sensorLat, sensorLon),
      });
    }
  }

  return [...latestById.values()].sort((a, b) => a.distanceKm - b.distanceKm);
}
